// Draft — Tier-0 durable buffer (client-side).
//
// The web buffer is the single point where every working copy converges
// (manual edits + AI stream + dev-mode disk). This module makes that buffer
// DURABLE, RESUMABLE, and VISIBLE so unsaved work survives a reload, a crash,
// or an accidental tab-close — without an explicit Save.
//
// MODEL — the draft is a SNAPSHOT, not an edit log. It is a materialised
// current-state map `{ path -> latest contents }` of files touched since the
// app was opened, plus a set of deleted paths. Incremental edits COLLAPSE per
// path: ten edits to Home.js leave exactly one entry (the latest bytes).
// Restore = overlay the snapshot onto the loaded save (collective apply).
// This is why storage stays flat — writes overwrite in place, never append.
//
// STORAGE — IndexedDB (roomy, async) written on a short debounce while the page
// is alive, plus a SYNCHRONOUS localStorage "parachute" written on
// visibilitychange:hidden / pagehide. The parachute exists because a graceful
// close runs handlers but does NOT await async IndexedDB, whereas a synchronous
// localStorage write completes before teardown. A true process kill runs no
// code at all (uncatchable here) — that gap is owned by the Tier-1 server draft.
//
// The core is STORAGE-AGNOSTIC: a Tier-1 backend sink (multi-device continuity)
// swaps in behind the same snapshot shape + resolve logic. `base` is a content
// hash of the loaded save, so any device can tell a clean resume (base matches
// the current save) from a drift (the save moved underneath the draft).
//
// See docs/RECONCILIATION-STRATEGY.md §7. Mirrors collabClient's singleton
// seam style (setApply / setGetFiles / setReconcile) so it wires in alongside it.

import {
  classify,
  resolve as resolvePlan,
} from '@/features/editor/collaboration/reconcile/reconcile-engine';
import type { ReconcilePlan } from '@/features/editor/collaboration/reconcile/reconcile-engine';
import type { ReviewResult } from '@/features/editor/collaboration/reconcile/reconcile-review/ReconcileReview';
import { getBaseSaveId } from '@/features/editor/session/utils/save-base';

// ── Types ───────────────────────────────────────────────────────────────────

type FileMap = Record<string, { type?: string; contents: string } | null>;

interface DraftSnapshot {
  /** Content hash of the save this draft was built on top of. */
  base: string;
  /** Save id the draft was built on — fetched on drift so diff3 has a base. */
  baseSaveId?: string | null;
  /** Monotonic-ish stamp (ms) used to pick the freshest of several sources. */
  updatedAt: number;
  /** path -> latest contents (files touched since open, code only). */
  files: Record<string, string>;
  /** path -> CAS URL for ASSET files touched since open. Assets are durable
   *  by-reference (immutable content-addressed blobs), so the draft stores the
   *  URL pointer, not bytes — that's what makes an AI/manual asset survive a
   *  reload (CAS Phase 6). Only host-backed `~asset` URLs are recorded. */
  assets?: Record<string, string>;
  /** paths deleted since open (tombstones so restore removes them). */
  deleted: string[];
}

/** Presenter that shows the review modal for a plan and resolves with the choice. */
type PlanReconcileFn = (plan: ReconcilePlan, label: string) => Promise<ReviewResult>;
/** Apply an overlay to the buffer (mirrors collab.setApply). */
type ApplyFn = (updates: FileMap) => void;
/** Read the current buffer (code files only), mirrors collab.setGetFiles. */
type GetFilesFn = () => Record<string, { type: string; contents: string }>;

export interface DraftState {
  /** True iff there is unsaved local work not in the last save. */
  hasDraft: boolean;
  /** Number of files changed/deleted vs the last save. */
  changedCount: number;
  /** True iff a pre-join backup exists (you joined a live session with unsaved
   *  work; it was stashed and can be restored after you leave). */
  hasPrejoinBackup: boolean;
}

/** Full pre-join code buffer, stashed when JOINing a live session (adopt-host)
 *  so your own divergence survives and can be restored/reconciled on leave. */
export interface PrejoinBackup {
  /** Full pre-join code buffer (path -> contents, assets excluded). */
  files: Record<string, string>;
  /** Save the pre-join buffer was based on (lets restore diff3 with a base). */
  baseSaveId?: string | null;
  updatedAt: number;
}

// ── Fast content hash (cyrb53) ────────────────────────────────────────────────
// Non-cryptographic 53-bit hash; a cheap fingerprint of "what the bytes are".
// Same primitive devBridge/collab use for echo-suppression (extract later).

function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/** Hash a file map deterministically (sorted paths, code only) → the base token. */
function hashFiles(files: Record<string, { type?: string; contents: string } | null>): string {
  const parts: string[] = [];
  for (const path of Object.keys(files).sort()) {
    const f = files[path];
    if (f && f.type !== 'ASSET' && typeof f.contents === 'string') {
      parts.push(path + '' + f.contents);
    }
  }
  return String(cyrb53(parts.join('\n')));
}

// ── Module state ──────────────────────────────────────────────────────────────

const DB_NAME = 'tile-drafts';
const STORE = 'drafts';
const DEBOUNCE_MS = 800;

let applyFn: ApplyFn | null = null;
let getFilesFn: GetFilesFn | null = null;
let planReconcileFn: PlanReconcileFn | null = null;

let key: string | null = null; // storage key for the current (app, branch, version)
let snap: DraftSnapshot | null = null; // in-memory accumulating snapshot
// Per-file hash of the base save (path -> cyrb53). Lets note() prune a file
// back out of the draft when it's edited back to exactly the saved content —
// so "change a letter and undo it" leaves no phantom draft. Cheap: a few bytes
// per file, not a second copy of the code.
let baseHashes: Record<string, string> = {};
let dirty = false; // is there un-flushed work?
let loading = false; // true while a save is being bulk-loaded into the buffer
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let prejoinPresent = false; // cached: is there a pre-join backup for `key`?
// Paused while a GUEST is in a live session: their live edits are the shared
// session, not their private draft (which is the pre-session one, stashed). The
// host is never paused — its draft is real, ongoing, savable work.
let paused = false;

const listeners = new Set<(s: DraftState) => void>();

// ── Version scope ─────────────────────────────────────────────────────────────
//
// A VERSION is a branch of the app: it owns its code (a saveId) as much as its
// content. So unsaved work belongs to the version it was typed against, and the
// storage key carries the version alongside (app, branch).
//
// That is what lets a version switch be free. Before this, switching had to
// force a real save of whatever was in flight, because there was ONE draft slot
// per (app, branch): loading the target's code over the top would have
// destroyed work with nowhere else to live. With a slot per version, leaving
// parks the draft under its own key and arriving restores the target's — no
// save, no save-spam in the history, and nothing lost either way.
//
// Scope is DECLARED before the first baseline rather than passed in, because
// the two facts arrive from different places at different times: App.tsx knows
// the open context, EditorShell learns the editing version from an async fetch.
// resolveOnOpen waits (bounded) for the declaration rather than baselining
// under a key it would immediately have to move.
let versionScope: string | null = null;
let scopeDeclared = false;
// Remembered from the last baseline so a version switch can re-run it without
// the caller re-deriving what it already told us.
let openAppId = 'default';
let openBranch: string | null = null;

/** The storage key for one draft slot. Unversioned form is the legacy key. */
function storageKey(appId: string, branch: string | null): string {
  const base = `${appId}:${branch || 'cloud'}`;
  return versionScope ? `${base}:v:${versionScope}` : base;
}

/** The version the draft currently belongs to (null = not version-scoped). */
export function getVersionScope(): string | null {
  return versionScope;
}

/**
 * Name the version this draft belongs to, BEFORE the first baseline.
 *
 * Called once per open, as soon as the editing version is known. Pass null when
 * versions could not be resolved — that is a real answer ("this app is not
 * version-scoped"), and it releases resolveOnOpen immediately instead of making
 * it sit out its whole retry budget.
 */
export async function declareVersionScope(id: string | null): Promise<void> {
  const alreadyBaselined = key !== null;
  versionScope = id;
  scopeDeclared = true;
  // The ordinary case: resolveOnOpen is still waiting, and will baseline under
  // the right key on its next tick.
  if (!alreadyBaselined) return;
  // We baselined before the scope arrived — the wait timed out, or this app was
  // already open. Park what we have and re-baseline under the version's key;
  // legacy adoption stays on, so that parked draft is exactly what comes back.
  await park();
  await resolveOnOpen(openAppId, openBranch, 0, true);
}

/** Persist the current draft NOW, so it survives whatever happens to the buffer
 *  next. Cheap and safe to call spuriously — a clean draft flushes nothing. */
export async function park(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  await flush();
}

/**
 * Move the draft to another version.
 *
 * MUST be called AFTER the target's code is in the buffer: the resume decision
 * is "does this draft sit on the save that is loaded", so it has to see the
 * loaded save. Parks the outgoing draft under its own key first, then
 * baselines on the new buffer and restores the target's parked work (or runs
 * the ordinary drift reconcile if the version's code moved underneath it).
 */
export async function switchVersionScope(id: string | null): Promise<void> {
  await park();
  versionScope = id;
  scopeDeclared = true;
  // No legacy adoption here: we just parked a draft under the OUTGOING key, and
  // if that key happened to be the unversioned one (scope was never declared),
  // adopting it would hand the version we are leaving its work to the version
  // we are opening. Migration belongs to the first baseline, not to a switch.
  await resolveOnOpen(openAppId, openBranch, 0, false);
}

// ── Seams (wired by App.tsx / EditorViewV2, mirroring collabClient) ───────────

export function setApply(fn: ApplyFn | null): void {
  applyFn = fn;
}
export function setGetFiles(fn: GetFilesFn | null): void {
  getFilesFn = fn;
}
export function setReconcilePlan(fn: PlanReconcileFn | null): void {
  planReconcileFn = fn;
}

/** Recompute the draft snapshot from the CURRENT buffer vs the base save — used
 *  on resume so drafting reflects whatever the buffer became while paused (e.g.
 *  a guest's adopted+co-edited session state after leaving). */
function rebaselineFromBuffer(): void {
  if (!snap || !getFilesFn) return;
  const files = getFilesFn();
  const newFiles: Record<string, string> = {};
  const newAssets: Record<string, string> = {};
  for (const [p, f] of Object.entries(files)) {
    if (!f || typeof f.contents !== 'string') continue;
    if ((f as { type?: string }).type === 'ASSET') {
      // Keep only durable hosted pointers (see noteAsset).
      if (HOSTED_ASSET_URL.test(f.contents)) newAssets[p] = f.contents;
    } else if (baseHashes[p] !== String(cyrb53(f.contents))) {
      newFiles[p] = f.contents; // changed or new vs save
    }
  }
  const deleted = Object.keys(baseHashes).filter((p) => !(p in files));
  snap.files = newFiles;
  snap.assets = newAssets;
  snap.deleted = deleted;
  snap.updatedAt = Date.now();
  dirty = true;
  scheduleFlush();
  notify();
}

/** Does the CURRENT buffer differ from the last save? (Used on guest-leave to
 *  decide whether there's anything to keep/discard.) Reads live, so it's valid
 *  even while paused. */
export function hasBufferChanges(): boolean {
  if (!getFilesFn) return false;
  const files = getFilesFn();
  for (const [p, f] of Object.entries(files)) {
    if (f && (f as { type?: string }).type !== 'ASSET' && typeof f.contents === 'string') {
      if (baseHashes[p] !== String(cyrb53(f.contents))) return true;
    }
  }
  for (const p of Object.keys(baseHashes)) if (!(p in files)) return true; // a save file was deleted
  return false;
}

/** Pause/resume draft tracking. Set true for a GUEST entering a live session
 *  (their edits are the shared session, not a private draft); false on leave —
 *  which rebaselines so the current buffer becomes their draft again. */
export function setPaused(p: boolean): void {
  if (p === paused) return;
  paused = p;
  if (!p) rebaselineFromBuffer();
}

/** Bracket a bulk save-load so its updateFiles calls aren't recorded as edits.
 *  The openContext host wraps replaceFiles in beginLoad()/endLoad(). */
export function beginLoad(): void {
  loading = true;
}
export function endLoad(): void {
  loading = false;
}

export function subscribe(fn: (s: DraftState) => void): () => void {
  listeners.add(fn);
  fn(getState());
  return () => listeners.delete(fn);
}

export function getState(): DraftState {
  const changedCount = snap
    ? Object.keys(snap.files).length +
      snap.deleted.length +
      (snap.assets ? Object.keys(snap.assets).length : 0)
    : 0;
  return { hasDraft: changedCount > 0, changedCount, hasPrejoinBackup: prejoinPresent };
}

/** The exact paths the draft diverges from the base save on. Discard needs this
 *  to be CHEAP: the snapshot already knows the handful of files that changed, so
 *  a revert can fetch and apply only those instead of re-downloading the whole
 *  save (which is what made it slow enough that users couldn't tell it had run). */
export interface DraftChanges {
  /** Code paths added or modified vs the base save. */
  files: string[];
  /** Asset paths added or modified vs the base save (CAS pointers). */
  assets: string[];
  /** Paths present in the base save that the draft deleted. */
  deleted: string[];
  /** The save the draft is based on — where reverted contents come from. */
  baseSaveId: string | null;
}

export function getChanges(): DraftChanges {
  return {
    files: snap ? Object.keys(snap.files) : [],
    assets: snap && snap.assets ? Object.keys(snap.assets) : [],
    deleted: snap ? snap.deleted.slice() : [],
    baseSaveId: snap ? (snap.baseSaveId ?? null) : null,
  };
}

function notify(): void {
  const s = getState();
  listeners.forEach((l) => {
    try {
      l(s);
    } catch {
      /* ignore listener errors */
    }
  });
}

// ── IndexedDB helpers (tiny promise wrappers) ─────────────────────────────────

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('no-idb'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(k: string, v: DraftSnapshot): Promise<void> {
  try {
    const db = await idbOpen();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(v, k);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* IDB unavailable (private mode, etc.) — parachute/Tier-1 still cover us */
  }
}

async function idbGet(k: string): Promise<DraftSnapshot | null> {
  try {
    const db = await idbOpen();
    const v = await new Promise<DraftSnapshot | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(k);
      req.onsuccess = () => resolve((req.result as DraftSnapshot) || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return v;
  } catch {
    return null;
  }
}

async function idbDel(k: string): Promise<void> {
  try {
    const db = await idbOpen();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(k);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    db.close();
  } catch {
    /* ignore */
  }
}

// ── localStorage parachute (synchronous, graceful-close only) ─────────────────

function lsKey(k: string): string {
  return 'tile:draft:' + k;
}

/** Synchronous last-gasp write — safe to call in a visibilitychange/pagehide
 *  handler because localStorage completes before teardown. Payload is tiny
 *  (touched code files only), so it stays well inside any unload watchdog. */
function parachuteWrite(): void {
  if (!key || !snap || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(lsKey(key), JSON.stringify(snap));
  } catch {
    /* quota / disabled — ignore */
  }
}

function parachuteRead(k: string): DraftSnapshot | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(lsKey(k));
    return raw ? (JSON.parse(raw) as DraftSnapshot) : null;
  } catch {
    return null;
  }
}

function parachuteClear(k: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(lsKey(k));
  } catch {
    /* ignore */
  }
}

// ── Pre-join backup (multiplayer adopt-host) ──────────────────────────────────
// Joining a live session adopts the host's state WHOLESALE. Before that, the
// joiner's own pre-join buffer is stashed here — a FULL code snapshot under a
// SEPARATE key so a keystroke after leaving (which overwrites the normal draft)
// can never clobber it. Restore = reconcile the current buffer against this
// backup through the same review modal (see EditorViewV2). Short-lived:
// localStorage only (small, synchronous, survives reload), cleared on
// restore/dismiss/next clean save.

function prejoinLsKey(k: string): string {
  return 'tile:draft:' + k + ':prejoin';
}

/** Stash the current (pre-adopt) buffer as the pre-join backup. Called by the
 *  adopt-host seam right before the joiner overwrites its buffer with the host's. */
export function backupPrejoin(): void {
  if (!key || !getFilesFn || typeof localStorage === 'undefined') return;
  const files = getFilesFn();
  const code: Record<string, string> = {};
  for (const [p, f] of Object.entries(files)) {
    if (f && (f as { type?: string }).type !== 'ASSET' && typeof f.contents === 'string') {
      code[p] = f.contents;
    }
  }
  const backup: PrejoinBackup = { files: code, baseSaveId: getBaseSaveId(), updatedAt: Date.now() };
  try {
    localStorage.setItem(prejoinLsKey(key), JSON.stringify(backup));
    prejoinPresent = true;
    notify();
  } catch {
    /* quota / disabled — backup is best-effort */
  }
}

/** Read the pre-join backup for the current (app, branch), if any. */
export function getPrejoinBackup(): PrejoinBackup | null {
  if (!key || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(prejoinLsKey(key));
    return raw ? (JSON.parse(raw) as PrejoinBackup) : null;
  } catch {
    return null;
  }
}

/** Drop the pre-join backup (after restore, dismiss, or a clean save). */
export function clearPrejoin(): void {
  if (!key || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(prejoinLsKey(key));
  } catch {
    /* ignore */
  }
  if (prejoinPresent) {
    prejoinPresent = false;
    notify();
  }
}

/** Return the buffer to the pre-session work stashed at join (consumed when a
 *  guest LEAVES a live session — the shared session code was the host's; the
 *  guest gets their own work back). Overwrites the buffer with the backup,
 *  re-baselines the draft to it, clears the backup, and unpauses. Returns false
 *  if there was no backup (the "Discard & join" case → caller keeps session
 *  state as the draft instead). */
export function restorePrejoinToBuffer(): boolean {
  const backup = getPrejoinBackup();
  if (!backup || !applyFn) {
    paused = false;
    return false;
  }
  const mine = getFilesFn ? getFilesFn() : {};
  const updates: FileMap = {};
  for (const [p, c] of Object.entries(backup.files)) updates[p] = { type: 'CODE', contents: c };
  // Remove code files present in the session buffer but not in my pre-session work.
  for (const [p, f] of Object.entries(mine)) {
    if (f && (f as { type?: string }).type !== 'ASSET' && !(p in backup.files)) updates[p] = null;
  }
  loading = true; // these applies are a restore, not user edits
  try {
    applyFn(updates);
  } finally {
    loading = false;
  }
  // Re-baseline the draft snapshot to the restored (pre-session) buffer.
  const newFiles: Record<string, string> = {};
  for (const [p, c] of Object.entries(backup.files)) {
    if (baseHashes[p] !== String(cyrb53(c))) newFiles[p] = c;
  }
  const deleted = Object.keys(baseHashes).filter((p) => !(p in backup.files));
  if (snap) {
    snap.files = newFiles;
    snap.deleted = deleted;
    snap.updatedAt = Date.now();
  }
  clearPrejoin();
  paused = false;
  dirty = true;
  scheduleFlush();
  notify();
  return true;
}

// ── Flush (debounced while alive) ─────────────────────────────────────────────

function scheduleFlush(): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, DEBOUNCE_MS);
}

async function flush(): Promise<void> {
  if (!key || !snap || !dirty) return;
  dirty = false;
  // All edits reverted to the saved state → no draft; drop the stored record
  // instead of leaving an empty one.
  if (
    Object.keys(snap.files).length === 0 &&
    snap.deleted.length === 0 &&
    !(snap.assets && Object.keys(snap.assets).length)
  ) {
    await idbDel(key);
    parachuteClear(key);
    return;
  }
  await idbPut(key, snap);
}

// ── Public: record an edit / deletion (called from updateFiles) ───────────────

/** Record a buffer change into the draft snapshot. `contents === null` = delete.
 *  Skips ASSET files — assets are durable by-reference (backend/hosted), not in
 *  the draft. Overwrites per path, so repeated edits collapse to the latest. */
export function note(path: string, contents: string | null): void {
  if (loading) return; // a save is being bulk-loaded — not a user edit
  if (paused) return; // guest in a live session — session edits aren't a private draft
  if (!snap) return; // not configured yet (no app open)
  const baseH = baseHashes[path]; // undefined ⇒ file not present in the base save
  if (contents === null) {
    // Deletion. Only a real change if the file EXISTED in the base save;
    // deleting a never-saved file is a net-zero and drops out of the draft.
    delete snap.files[path];
    const di = snap.deleted.indexOf(path);
    if (baseH !== undefined) {
      if (di < 0) snap.deleted.push(path);
    } else if (di >= 0) {
      snap.deleted.splice(di, 1);
    }
  } else {
    const di = snap.deleted.indexOf(path);
    if (di >= 0) snap.deleted.splice(di, 1);
    if (baseH !== undefined && baseH === String(cyrb53(contents))) {
      // Edited back to exactly the saved content — no longer a change.
      delete snap.files[path];
    } else {
      snap.files[path] = contents;
    }
  }
  snap.updatedAt = Date.now();
  dirty = true;
  scheduleFlush();
  notify();
}

/** A durable, host-backed asset URL (our `~asset` hosting incl. content-addressed
 *  `~asset/cas/<hash>`). Only these belong in the draft — a dev-mode disk path or
 *  a base64 blob is NOT a stable pointer, so we don't persist it. */
const HOSTED_ASSET_URL = /^https?:\/\/\S*~asset/;

/** Record an ASSET change into the draft as its CAS URL pointer (or a deletion
 *  when `contents === null`). Mirrors note() but keeps assets in `snap.assets`
 *  (by-reference) so they restore on reload without bloating the draft with
 *  bytes. Non-hosted contents (disk path / base64) are skipped — the asset still
 *  lives in the buffer, it just isn't a durable draft pointer. */
export function noteAsset(path: string, contents: string | null): void {
  if (loading) return; // bulk save-load, not a user edit
  if (paused) return; // guest in a live session
  if (!snap) return; // no app open yet
  if (!snap.assets) snap.assets = {};
  if (contents === null) {
    // Deletion — drop the pointer and tombstone the path so restore removes it.
    // (Removing a path the save never had is a harmless no-op on restore.)
    delete snap.assets[path];
    if (snap.deleted.indexOf(path) < 0) snap.deleted.push(path);
  } else if (HOSTED_ASSET_URL.test(contents)) {
    const di = snap.deleted.indexOf(path);
    if (di >= 0) snap.deleted.splice(di, 1);
    snap.assets[path] = contents;
  } else {
    return; // not a durable hosted URL — don't persist
  }
  snap.updatedAt = Date.now();
  dirty = true;
  scheduleFlush();
  notify();
}

// ── Resume/reconcile decision on open ─────────────────────────────────────────

/** Fetch a save's CODE files (path -> contents) from the backend. Used on drift
 *  to get the draft's ancestor content so the engine can three-way merge. */
async function fetchSaveCode(appId: string, saveId: string): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  try {
    const mres = await fetch(`/api/apps/${appId}/saves/${saveId}`, { credentials: 'same-origin' });
    if (!mres.ok) return out;
    const mj = await mres.json();
    const raw = mj.manifest?.files || [];
    const entries = (Array.isArray(raw) ? raw : Object.values(raw)) as {
      path: string;
      type?: string;
    }[];
    await Promise.all(
      entries
        .filter((e) => e && e.path && e.type !== 'ASSET')
        .map(async (e) => {
          try {
            const r = await fetch(
              `/api/apps/${appId}/saves/${saveId}/file?path=${encodeURIComponent(e.path)}`,
              { credentials: 'same-origin' },
            );
            if (r.ok) out[e.path] = await r.text();
          } catch {
            /* skip */
          }
        }),
    );
  } catch {
    /* skip */
  }
  return out;
}

/** Overlay the draft snapshot onto the buffer (collective apply). */
function applyDraft(s: DraftSnapshot): void {
  if (!applyFn) return;
  const updates: FileMap = {};
  for (const [p, c] of Object.entries(s.files)) updates[p] = { type: 'CODE', contents: c };
  // Assets as by-reference CAS URLs — restores an AI/manual asset after reload.
  if (s.assets)
    for (const [p, url] of Object.entries(s.assets)) updates[p] = { type: 'ASSET', contents: url };
  // Deletions last so a re-added path isn't nulled by a stale tombstone.
  for (const p of s.deleted) updates[p] = null;
  if (Object.keys(updates).length) applyFn(updates);
}

/** Pick the freshest stored draft (localStorage parachute vs IndexedDB) and, if
 *  the parachute is newer, promote it into IndexedDB and clear it. */
async function loadStored(k: string, legacyKey?: string): Promise<DraftSnapshot | null> {
  const fromIdb = await idbGet(k);
  const fromLs = parachuteRead(k);
  let chosen: DraftSnapshot | null = fromIdb;
  if (fromLs && (!fromIdb || fromLs.updatedAt > fromIdb.updatedAt)) {
    chosen = fromLs;
    await idbPut(k, fromLs); // promote parachute → durable store
  }
  parachuteClear(k); // parachute is one-shot; drop it once read
  // Nothing under the versioned key — adopt a draft left by the unversioned
  // scheme (a session that predates version scoping). It was typed against
  // whichever version was open then, so the first version opened after the
  // upgrade is the best available claim on it; the move is one-way, so no
  // other version can adopt it afterwards. A wrong guess is not a loss: the
  // base hash won't match and it lands in the ordinary drift reconcile.
  if (!chosen && legacyKey) {
    const legacy = await loadStored(legacyKey);
    if (legacy) {
      await idbPut(k, legacy);
      await idbDel(legacyKey);
      chosen = legacy;
    }
  }
  return chosen &&
    (Object.keys(chosen.files).length ||
      chosen.deleted.length ||
      (chosen.assets && Object.keys(chosen.assets).length))
    ? chosen
    : null;
}

/**
 * Called after a save is loaded into the buffer on open. Decides:
 *  - clean resume (draft.base === hash of loaded save) → auto-apply overlay,
 *  - drift (base differs → the save moved) → reconcile modal (keep mine / take saved),
 *  - no draft → start a fresh snapshot baselined on the loaded save.
 */
export async function resolveOnOpen(
  appId: string,
  branch: string | null,
  attempt = 0,
  adoptLegacy = true,
): Promise<void> {
  if (!getFilesFn) return;
  openAppId = appId;
  openBranch = branch;
  const savedFiles = getFilesFn();
  // Two things have to be true before we can baseline, and both arrive late:
  //
  //  - the buffer must be seeded into tilePacket (the outer App loads
  //    state.files, then MainContainer seeds it), so `base` reflects real
  //    content — that is what makes clean-resume vs drift correct;
  //  - the version scope must be declared, so the draft is read from and
  //    written to the slot that belongs to the version actually open.
  //
  // Retry ~3s then proceed with what we have. Giving up on the scope falls back
  // to the unversioned key, which is exactly the pre-versions behaviour.
  if ((Object.keys(savedFiles).length === 0 || !scopeDeclared) && attempt < 20) {
    setTimeout(() => void resolveOnOpen(appId, branch, attempt + 1, adoptLegacy), 150);
    return;
  }
  const base = hashFiles(savedFiles as any);
  // Per-file base hashes for note()'s "edited back to saved" pruning.
  baseHashes = {};
  for (const [p, f] of Object.entries(savedFiles)) {
    if (f && typeof f.contents === 'string') baseHashes[p] = String(cyrb53(f.contents));
  }
  const k = storageKey(appId, branch);
  key = k;
  // Re-detect a pre-join backup stashed before a reload (survives in localStorage).
  prejoinPresent = !!getPrejoinBackup();

  // Fresh baseline for this session; replaced below if we resume a stored draft.
  snap = {
    base,
    baseSaveId: getBaseSaveId(),
    updatedAt: Date.now(),
    files: {},
    assets: {},
    deleted: [],
  };
  dirty = false;

  const stored = await loadStored(
    k,
    adoptLegacy && versionScope ? `${appId}:${branch || 'cloud'}` : undefined,
  );
  if (!stored) {
    notify();
    return;
  }

  if (stored.base === base) {
    // Clean resume — the draft sits on exactly this save. Auto-apply silently.
    applyDraft(stored);
    snap = stored; // continue accumulating on it
    notify();
    return;
  }

  // Drift — the save moved underneath the draft. Reconcile via the shared engine
  // (auto-merge disjoint + diff3 same-file; review only true conflicts).
  const savedMap: Record<string, string> = {}; // theirs = the current save
  for (const [p, f] of Object.entries(savedFiles)) savedMap[p] = f.contents;

  // Reconstruct "mine" = the draft's ancestor overlaid with my draft changes.
  // Fetch the ancestor's content so diff3 can distinguish my-only edits from
  // both-sides edits (without it, all my edits would look like conflicts).
  let baseMap: Record<string, string> | undefined;
  if (stored.baseSaveId) {
    const fetched = await fetchSaveCode(appId, stored.baseSaveId);
    if (Object.keys(fetched).length) baseMap = fetched;
  }
  const mine: Record<string, string> = { ...(baseMap || savedMap) };
  for (const [p, c] of Object.entries(stored.files)) mine[p] = c;
  for (const p of stored.deleted) delete mine[p];

  const plan = classify(mine, savedMap, baseMap);
  const res: ReviewResult = planReconcileFn
    ? await planReconcileFn(plan, 'your unsaved changes')
    : { action: 'theirs' };

  if (res.action === 'cancel') {
    // Keep the saved buffer loaded; keep the draft on disk to re-prompt later.
    notify();
    return;
  }
  if (res.action === 'theirs') {
    await clear(); // discard my draft; keep the saved buffer as-is
    return;
  }

  // 'merge' → combine per choices; 'override' → my draft wins wholesale.
  const resolved = res.action === 'override' ? mine : resolvePlan(plan, res.choices);

  // Apply the resolved tree onto the buffer (currently the saved files).
  if (applyFn) {
    const updates: FileMap = {};
    for (const [p, c] of Object.entries(resolved)) {
      if (savedMap[p] !== c) updates[p] = { type: 'CODE', contents: c };
    }
    for (const p of Object.keys(savedMap)) {
      if (!(p in resolved)) updates[p] = null;
    }
    // Assets are by-reference and don't participate in the code drift merge —
    // carry the draft's asset pointers straight through so they survive.
    if (stored.assets) {
      for (const [p, url] of Object.entries(stored.assets))
        updates[p] = { type: 'ASSET', contents: url };
    }
    if (Object.keys(updates).length) applyFn(updates);
  }

  // Re-baseline the draft on the current save (its diff becomes the new draft).
  baseHashes = {};
  for (const [p, c] of Object.entries(savedMap)) baseHashes[p] = String(cyrb53(c));
  const newFiles: Record<string, string> = {};
  for (const [p, c] of Object.entries(resolved)) if (savedMap[p] !== c) newFiles[p] = c;
  const newDeleted = Object.keys(savedMap).filter((p) => !(p in resolved));
  snap = {
    base,
    baseSaveId: getBaseSaveId(),
    updatedAt: Date.now(),
    files: newFiles,
    assets: stored.assets || {},
    deleted: newDeleted,
  };
  dirty = true;
  scheduleFlush();
  notify();
}

// ── Clear (on save success or discard) ────────────────────────────────────────

/** Discard the draft. After a Save the buffer IS the new saved state, so we
 *  re-baseline the in-memory snapshot on it (empty, base = current buffer). */
export async function clear(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (key) {
    await idbDel(key);
    parachuteClear(key);
    // A save commits the current buffer as truth — the pre-session backup is
    // now stale; drop it so the "restore?" affordance doesn't linger.
    clearPrejoin();
  }
  const files = getFilesFn ? getFilesFn() : {};
  const base = getFilesFn ? hashFiles(files as any) : snap?.base || '';
  // Re-baseline per-file hashes on the just-saved buffer.
  baseHashes = {};
  for (const [p, f] of Object.entries(files)) {
    if (f && typeof f.contents === 'string') baseHashes[p] = String(cyrb53(f.contents));
  }
  snap = {
    base,
    baseSaveId: getBaseSaveId(),
    updatedAt: Date.now(),
    files: {},
    assets: {},
    deleted: [],
  };
  dirty = false;
  notify();
}

/** User clicked "Discard" on the draft badge — drop it and reload the save.
 *  The caller (indicator) is responsible for re-opening/replacing the buffer. */
export async function discard(): Promise<void> {
  await clear();
}

// ── Lifecycle: flush on the last reliable events ──────────────────────────────
// visibilitychange→hidden is the ONE event that fires across desktop-close,
// tab-switch, and mobile-background — treat it as "I might be dying". pagehide
// backs it up for teardown. Both do the synchronous parachute write + kick the
// async IDB flush.

let lifecycleBound = false;
export function bindLifecycle(): () => void {
  if (lifecycleBound || typeof document === 'undefined') return () => undefined;
  lifecycleBound = true;
  const onHidden = () => {
    if (document.visibilityState === 'hidden') {
      parachuteWrite();
      void flush();
    }
  };
  const onPageHide = () => {
    parachuteWrite();
    void flush();
  };
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', onPageHide);
    lifecycleBound = false;
  };
}
